import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiUsers } from 'react-icons/fi';
import { TiEdit, TiTrash } from 'react-icons/ti';
import { useQueryClient } from 'react-query';
import Swal from 'sweetalert2';

import { DataTable, SearchBar, StyledButton } from '../../../../components';
import { Loading } from '../../../../components/Loading';
import { TableActionButton } from '../../../../components/TableActionButton';
import { useAuth } from '../../../../hooks/auth';
import {
  deleteUser,
  useUsuarios,
} from '../../../../services/hooks/useUsuarios';
import { CnpjMask } from '../../../../utils/CnpjMask';
import { CpfMask } from '../../../../utils/CpfMask';
import { AdminTitle } from '../../components';
import { UserForm } from '../../components/UserForm';
import { Row, UsuariosContainer } from './styles';

const tableHead = ['Nome', 'Email', 'Documento', 'Telefone', 'Perfil', 'Ações'];

export interface User {
  id: number;
  sub: string;
  cnpj: string;
  email: string;
  phone: string;
  profile: string;
  name: string;
  documents?: {
    id: number;
    document: string;
    created_at: string;
    updated_at: string;
    deleted_at?: string;
  }[];
}

export const Pessoas = () => {
  const queryClient = useQueryClient();

  const [users, setUsers] = useState<User[]>([]);

  const [currentUser, setCurrentUser] = useState<User>();

  const [actualPage, setActualPage] = useState(1);

  const [resultsPerPage, setResultsPerPage] = useState(10);

  const [text, setText] = useState('');

  const [formView, setFormView] = useState(false);

  const [loading, setLoading] = useState(false);

  const [previousPage, setPreviousPage] = useState(1);

  const { user: loggedUser } = useAuth();

  const params = useMemo(
    () => ({
      page: actualPage,
      resultsPerPage,
      text: text,
    }),
    [actualPage, resultsPerPage, text]
  );

  const { data, isLoading, refetch } = useUsuarios({
    ...params,
    actualPage,
    resultsPerPage,
    textParams: text,
  });

  useEffect(() => {
    if (data) {
      const { actualPage: actPage, results, resultsPerPage: nPerPage } = data;

      if (previousPage === 1 && actPage === 1) {
        setUsers([...results]);
      } else if (previousPage < actPage) {
        setUsers([...users, ...results]);
      } else if (previousPage > actPage) {
        let tempUsers = [...users];
        tempUsers = tempUsers?.splice(0, actPage * resultsPerPage);
        setUsers(tempUsers);
      }

      setResultsPerPage(nPerPage);
      setActualPage(actPage);
    }
  }, [actualPage, data]);

  useEffect(() => {
    refetch();
  }, [formView, refetch]);

  const handleInputValue = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  }, []);

  // const debouncedChangeHandler = useMemo(() => debounce(handleInputValue), []);

  const setPage = (value: any) => {
    setPreviousPage(actualPage);
    setActualPage(value);
  };

  const setTotalItemsPerPage = (value: any) => {
    setResultsPerPage(value);
    setActualPage(1);
    setPreviousPage(1);

    if (data) {
      const { results } = data;
      setUsers([...results]);
    }
  };

  const handleDeleteUser = async (user: User) => {
    Swal.fire({
      title: 'Excluir Pessoa',
      html: `Realmente deseja excluir o usuário ${user.name}?`,
      showConfirmButton: true,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#F58220',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);

        try {
          await deleteUser(user.id);

          queryClient.removeQueries({
            queryKey: 'users',
          });

          setText('');
          setTotalItemsPerPage(resultsPerPage);
          setLoading(false);
          refetch();

          Swal.fire({
            title: 'Sucesso!',
            html: `${user.name} foi excluído com sucesso!`,
            confirmButtonColor: '#F58220',
          });
        } catch (error: any) {
          setLoading(false);
          const message = error.response?.data?.message
            ? error.response.data.message
            : 'Erro ao excluir o usuário';
          Swal.fire({
            title: 'Erro - Tente novamente!',
            html: `Não foi possível excluir o usuário ${user.name}! </br> ${message}`,
            confirmButtonColor: '#F58220',
          });
        }
      }
    });
  };

  return (
    <>
      {isLoading && <Loading />}
      {loading && <Loading />}
      <UsuariosContainer>
        <AdminTitle title="Pessoas" icon={<FiUsers />} />
        {formView ? (
          <UserForm
            setFormView={setFormView}
            user={currentUser}
            setUser={setCurrentUser}
          />
        ) : (
          <>
            <div className="searchBarContainer">
              <SearchBar handleInput={handleInputValue} value={text} />
              <StyledButton
                icon={<FiPlus />}
                iconPosition="left"
                text="Cadastrar"
                onClick={() => setFormView(true)}
              />
            </div>

            <DataTable
              title="Detalhado"
              heads={tableHead}
              hasPagination
              setPage={setPage}
              page={actualPage}
              setItemsPerPage={setTotalItemsPerPage}
              totalItems={data?.results?.length}
              itemsPerPage={resultsPerPage}
            >
              <tbody>
                {users?.map((user) => (
                  <Row key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      {user?.cnpj?.length <= 11
                        ? CpfMask(user?.cnpj?.padStart(11, '0'))
                        : CnpjMask(user?.cnpj?.padStart(14, '0'))}
                    </td>
                    <td>
                      {user.phone
                        .substring(3, 99)
                        .replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')}
                    </td>
                    <td>{user.profile}</td>
                    <td>
                      <TableActionButton
                        label="Editar pessoa"
                        icon={<TiEdit />}
                        onClick={() => {
                          setCurrentUser(user);
                          setFormView(true);
                        }}
                      />
                      {loggedUser.profile === 'admin' && (
                        <TableActionButton
                          label="Excluir pessoa"
                          icon={<TiTrash />}
                          onClick={() => {
                            handleDeleteUser(user);
                          }}
                        />
                      )}
                    </td>
                  </Row>
                ))}
              </tbody>
            </DataTable>
          </>
        )}
      </UsuariosContainer>
    </>
  );
};
